import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../../../api/countries/get-countries'
import { getFormulas } from '../../../../../api/formula/get-formulas'
import { getPeriods } from '../../../../../api/periods/get-periods'
import { OptionPriceInput } from '../../../../../lib/types/models/option-prices/option-price-input'
import { BaseFormProps } from '../../../../../lib/types/utils'
import { getSubmitAction } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../../../lib/utils/functions/translation.functions'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../../elements/forms/form'
import ControlledInput from '../../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../../../elements/validation-buttons'

const formSchema: ZodType<OptionPriceInput> = z
  .object({
    price: createNumberSchema('prix'),
    maxAge: createNumberSchema('âge max', { optional: true }),
    minAge: createNumberSchema('âge min', { optional: true }),
    appliedDurationsInDays: createNumberArraySchema('durée en jours'),
    countryIds: createNumberArraySchema('pays'),
    formulaIds: createNumberArraySchema('formule'),
    periodIds: createNumberArraySchema('période')
  })
  .superRefine((data, ctx) => {
    if (data.minAge && data.maxAge && data.minAge > data.maxAge) {
      ctx.addIssue({
        message: "L'âge max doit être supérieur à l'âge min",
        code: z.ZodIssueCode.custom,
        path: ['minAge']
      })
    }
    return {}
  })

const OptionPriceForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<OptionPriceInput, true>) => {
  const form = useForm<OptionPriceInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', MODAL_BODY_CLASSNAME)}>
          <FormField
            control={form.control}
            name="formulaIds"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label="Formule(s)"
                    variant="primary"
                    options={
                      formulas?.data.map((formula) => ({
                        label: getTranslatedName(formula),
                        value: formula.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((formulaId) => ({
                        label: getTranslatedName(
                          formulas?.data.find((formula) => formula.id === formulaId)
                        ),
                        value: formulaId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="periodIds"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label="Périodes"
                    variant="warning"
                    options={
                      periods?.data.map((period) => ({
                        label: period.name,
                        value: period.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((periodId) => ({
                        label: periods?.data.find((period) => period.id === periodId)?.name ?? '',
                        value: periodId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="countryIds"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label="Pays"
                    variant="primary"
                    options={
                      countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((countryId) => ({
                        label:
                          countries?.data.find((country) => country.id === countryId)?.name ?? '',
                        value: countryId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="minAge"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="number" label="Âge min" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxAge"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="number" label="Âge max" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="appliedDurationsInDays"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    type="number"
                    label="Durée en jours"
                    variant="primary"
                    onChange={(value) => {
                      field.onChange(value.map((val) => Number(val)))
                    }}
                    selectedOptions={
                      field.value?.map((duration) => ({
                        label: duration.toString(),
                        value: duration
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput required {...field} type="number" label="Prix (€)" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </form>

      <ValidationButtons hasBorderTop {...validationButtonProps} />
    </Form>
  )
}

export default OptionPriceForm
